import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useLang } from "../lib/i18n";

function markerIcon(active = false) {
  return L.divIcon({
    className: "argali-marker-wrap",
    html: `<div class="argali-marker${active ? " active" : ""}"><span></span></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -12],
  });
}

export default function AltaiMap({ features = [], height = "600px", activeId, onSelectPlace, routeCoords }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const { lang } = useLang();

  useEffect(() => {
    if (!ref.current || mapRef.current) return;

    const map = L.map(ref.current, {
      zoomControl: true,
      scrollWheelZoom: true,
      attributionControl: true,
    }).setView([50.75, 86.95], 7);
    mapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 18,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    features.forEach((feature) => {
      const coords = feature?.geometry?.coordinates;
      const p = feature?.properties || {};
      if (!coords || coords.length < 2) return;

      const marker = L.marker([coords[1], coords[0]], { icon: markerIcon(p.id === activeId) }).addTo(map);
      markersRef.current[p.id] = marker;

      const name = lang === "en" ? p.name_en || p.name_ru : p.name_ru || p.name_en;
      const photo = p.photo_url
        ? `<img src="${p.photo_url}" alt="${name || "АРГАЛИ"}" style="width:100%;height:120px;object-fit:cover;margin-bottom:10px"/>`
        : "";
      const popup = `<div style="font-family:Arial,sans-serif;min-width:200px">
          ${photo}
          <div style="font-family:Georgia,serif;font-size:18px;color:#11110F;margin-bottom:4px">${name || ""}</div>
          <div style="font-size:11px;color:#776653;text-transform:uppercase;letter-spacing:.08em">${p.category || ""}</div>
        </div>`;
      marker.bindPopup(popup, { closeButton: true, maxWidth: 260 });

      marker.on("click", () => {
        if (onSelectPlace) onSelectPlace(p);
      });
    });

    if (routeCoords && routeCoords.length > 1) {
      const line = L.polyline(routeCoords, { color: "#A96818", weight: 2, opacity: 0.9, dashArray: "1 8", lineCap: "round" }).addTo(map);
      map.fitBounds(line.getBounds().pad(0.25));
    } else if (features.length) {
      const group = L.featureGroup(Object.values(markersRef.current));
      map.fitBounds(group.getBounds().pad(0.12));
    }

    return () => {
      map.remove();
      mapRef.current = null;
      markersRef.current = {};
    };
  }, [features.length, lang]);

  useEffect(() => {
    Object.entries(markersRef.current).forEach(([id, m]) => {
      const el = m.getElement()?.querySelector(".argali-marker");
      if (el) el.classList.toggle("active", id === activeId);
    });
    if (activeId && markersRef.current[activeId] && onSelectPlace) {
      const m = markersRef.current[activeId];
      mapRef.current?.panTo(m.getLatLng());
    }
  }, [activeId, onSelectPlace]);

  return <div ref={ref} data-testid="altai-map" style={{ height, width: "100%" }} />;
}
